import { ChangeDetectionStrategy, Component, OnInit, signal, input } from "@angular/core";
import { IonicModule, LoadingController, ModalController, ToastController } from "@ionic/angular";
import { ModeService } from "../../../utils/mode.service";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ReportGeneratorDto } from "../../interfaces/report_generator_dto";
import { DataService } from "src/app/supabase/services/data.service";
import { FileService } from "src/app/supabase/services/files.service";
import { CommonModule } from "@angular/common";
import { Store } from "@ngrx/store";
import { selectAllReportConfigurations, selectReportConfigurationsByScreen } from "../../ngrx/reports.selectors";
import { loadReportConfigurations, upsertReport } from "../../ngrx/reports.actions";
import { AuthService } from "src/app/supabase/services/auth.service";
import { ReportConfigurationDto } from "../../interfaces/report_configuration_dto";
import { Plans } from "src/app/shared/models/plans.enum";
import { selectEntitlements } from "src/app/auth/ngrx/customer.selectors";
import * as moment from 'moment';

@Component({
    selector: "app-report-generation",
    templateUrl: "./report-generation.component.html",
    styleUrls: ["./report-generation.component.scss"],
    imports: [IonicModule, ReactiveFormsModule, FormsModule, CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportGenerationComponent implements OnInit {
  readonly report = input<ReportGeneratorDto>(undefined);
  readonly studentName = input<string>(undefined);
  readonly screen = input<string>(undefined);
  reportConfigurationsSignal = this.store.selectSignal(selectAllReportConfigurations);
  customerEntitlementsSignal = this.store.selectSignal(selectEntitlements);
  timeRangeSelectionSignal = signal('all');
  reportForm: FormGroup;
  customDateRange: boolean;

  constructor(
    public modeService: ModeService,
    private fb: FormBuilder,
    private modalController: ModalController,
    private dataService: DataService,
    private loadingCtrl: LoadingController,
    private authService: AuthService,
    private fileService: FileService,
    private toastController: ToastController,
    private store: Store<any>,
  ) {
    this.reportForm = this.fb.group({
      start_date: [null], 
      end_date: [null],
      report_id: [null, Validators.required],
      user_id: [""],
      student_mail_direct: [false],
      student_id: [null],
      mail_direct: [true],
      storage_save: [true]
    });
  }

  ngOnInit() {
    const report = this.report();
    if (report) {
      this.reportForm.patchValue(report);
    }

    const screen = this.screen();
    if (screen) {
      this.reportConfigurationsSignal = this.store.selectSignal(selectReportConfigurationsByScreen(screen));
    }
    this.updateTimeSelection(this.reportForm.controls.report_id.value);
  }

  ionViewDidEnter() {
    this.store.dispatch(loadReportConfigurations());
  }

  hasAccess(reportConfiguration: ReportConfigurationDto) {
    if (this.customerEntitlementsSignal()?.length === 0) {
      return reportConfiguration.free;
    } else if (this.customerEntitlementsSignal()?.includes(Plans.basic) || this.customerEntitlementsSignal()?.includes(Plans.basic_promo)) {
      return reportConfiguration.basic;
    } else if (this.customerEntitlementsSignal()?.includes(Plans.premium) || this.customerEntitlementsSignal()?.includes(Plans.premium_promo)) {
      return reportConfiguration.premium;
    }
  }

  updateTimeSelection(report_id) {
    const defaultTimeRange = this.getReportDefaultTimeSpan(report_id) ?? 'all';
    this.timeRangeSelectionSignal.set(defaultTimeRange);
  }

  updateTimeSpan(selection) {
    switch (selection) {
    case "all":
      this.customDateRange = false;
      this.reportForm.get("start_date").setValue(null);
      this.reportForm.get("end_date").setValue(null);
      break;
    case "1w":
      this.customDateRange = false;
      // Current week (Monday to Sunday)
      const startOfWeek = moment().startOf('isoWeek');
      const endOfWeek = moment().endOf('isoWeek');
      this.reportForm.get("start_date").setValue(startOfWeek.format('YYYY-MM-DD'));
      this.reportForm.get("end_date").setValue(endOfWeek.format('YYYY-MM-DD'));
      break;
    case "2w":
      this.customDateRange = false;
      // Last week and current week
      const startOfLastWeek = moment().subtract(1, 'weeks').startOf('isoWeek');
      const endOfCurrentWeek = moment().endOf('isoWeek');
      this.reportForm.get("start_date").setValue(startOfLastWeek.format('YYYY-MM-DD'));
      this.reportForm.get("end_date").setValue(endOfCurrentWeek.format('YYYY-MM-DD'));
      break;
    case "1m":
      this.customDateRange = false;
      // Current month
      const startOfMonth = moment().startOf('month');
      const endOfMonth = moment().endOf('month');
      this.reportForm.get("start_date").setValue(startOfMonth.format('YYYY-MM-DD'));
      this.reportForm.get("end_date").setValue(endOfMonth.format('YYYY-MM-DD'));
      break;
    case "2m":
      this.customDateRange = false;
      // Last month
      const startOfLastMonth = moment().subtract(1, 'months').startOf('month');
      const endOfLastMonth = moment().subtract(1, 'months').endOf('month');
      this.reportForm.get("start_date").setValue(startOfLastMonth.format('YYYY-MM-DD'));
      this.reportForm.get("end_date").setValue(endOfLastMonth.format('YYYY-MM-DD'));
      break;
    case "1y":
      this.customDateRange = false;
      // Current year
      const startOfYear = moment().startOf('year');
      const endOfYear = moment().endOf('year');
      this.reportForm.get("start_date").setValue(startOfYear.format('YYYY-MM-DD'));
      this.reportForm.get("end_date").setValue(endOfYear.format('YYYY-MM-DD'));
      break;
    case "custom":
      this.customDateRange = true;
      break;
    }
  }

  getReportName(reportId: number): string {
    const reportConfiguration = this.reportConfigurationsSignal().find(rc => rc.report_id === reportId);
    return reportConfiguration?.report_name;
  }
  
  getReportDefaultTimeSpan(reportId: number): string {
    const reportConfiguration = this.reportConfigurationsSignal().find(rc => rc.report_id === reportId);
    return reportConfiguration?.default_time_range;
  }
  
  cancel() {
    this.modalController.dismiss();
  }

  async save() {
    if (this.reportForm.valid) {

      let report = this.reportForm.getRawValue() as ReportGeneratorDto;
      let userId = await this.authService.getCurrentUserId();
      report.file_name = `${this.getReportName(report.report_id)}_${moment().format('YYYY-MM-DD')}`;

      if (report.storage_save) {
        try {
          const loading = await this.loadingCtrl.create({
            message: "Bericht erstellen...",
          });

          loading.present();
          const newReport = await this.dataService.addReportGenerator(report).toPromise();
          var fileName = `${userId}/${newReport.report_generator_id}.pdf`;

          let file;
          let attempts = 0;
          const maxAttempts = 30;
          const downloadInterval = setInterval(async () => {
            attempts++;
            file = await this.fileService.downloadFile('reports', { path: fileName, title: ''});
            if (file && file instanceof Blob) {
              clearInterval(downloadInterval);
              loading.dismiss();
              await this.fileService.openFile(report.file_name, file, "application/pdf");
              newReport.is_generated = true;
              this.store.dispatch(upsertReport({report: newReport}));
            } else if (attempts >= maxAttempts) {
              clearInterval(downloadInterval);
              throw new Error("Failed to download file within 1 minute.");
            }
          }, 2000);
        } catch (error) {
          // show an error
          const toast = await this.toastController.create({
            message: "Die Berichterstellung ist fehlgeschlagen!",
            duration: 1500,
            position: "bottom",
            color: "danger",
          });

          await toast.present();
        }
      } else if (report.mail_direct || report.student_mail_direct) {
        this.dataService.addReportGenerator(report);
        const toast = await this.toastController.create({
          message: "Der Bericht wird per E-Mail zugestellt!",
          duration: 1500,
          position: "bottom",
          color: "success",
        });

        await toast.present();
      }

      this.modalController.dismiss();
    }
  }
}
